import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6rur544k19g20dvrr60', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', email: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative bg-[#1B2838] min-h-[600px] overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute inset-0 opacity-5">
        <svg className="w-full h-full" viewBox="0 0 1200 600">
          <path d="M0,300 Q300,200 600,300 T1200,300" stroke="white" strokeWidth="2" fill="none" />
          <path d="M0,350 Q300,250 600,350 T1200,350" stroke="white" strokeWidth="2" fill="none" />
        </svg>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Left Content Zone */}
          <div className="lg:w-1/2">
            <div className="bg-white/10 backdrop-blur-sm inline-block px-4 py-2 rounded-lg mb-8">
              <span className="text-white text-sm uppercase tracking-wide font-semibold">
                Get Your Free Estimate
              </span>
            </div>

            <h2 className="text-5xl md:text-6xl font-bold text-white mb-12 leading-tight">
              Ready to Protect Your Oklahoma Home?
            </h2>

            {/* Contact Form */}
            <form 
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-8"
            >
              <div className="space-y-5">
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full bg-transparent border-2 border-white text-white placeholder-white/60 px-4 py-3 rounded-lg focus:outline-none focus:border-[#FF6B35] transition-colors"
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  className="w-full bg-transparent border-2 border-white text-white placeholder-white/60 px-4 py-3 rounded-lg focus:outline-none focus:border-[#FF6B35] transition-colors"
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full bg-transparent border-2 border-white text-white placeholder-white/60 px-4 py-3 rounded-lg focus:outline-none focus:border-[#FF6B35] transition-colors"
                />
                <select
                  name="service"
                  value={formData.service}
                  onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                  required
                  className="w-full bg-transparent border-2 border-white text-white px-4 py-3 rounded-lg focus:outline-none focus:border-[#FF6B35] transition-colors cursor-pointer"
                >
                  <option value="" className="text-gray-800">Select Service</option>
                  <option value="Insurance Claim" className="text-gray-800">Insurance Claim Assistance</option>
                  <option value="Roof Replacement" className="text-gray-800">Complete Roof Replacement</option>
                  <option value="Storm Damage" className="text-gray-800">Storm Damage Repair</option>
                  <option value="Inspection" className="text-gray-800">Free Inspection</option>
                </select>
                <textarea
                  name="message"
                  placeholder="Tell us about your roofing needs..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  maxLength={500}
                  rows={4}
                  className="w-full bg-transparent border-2 border-white text-white placeholder-white/60 px-4 py-3 rounded-lg focus:outline-none focus:border-[#FF6B35] transition-colors resize-none"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full mt-6 bg-[#FF6B35] text-white py-4 rounded-full font-semibold text-lg hover:bg-[#ff5722] transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Sending...' : 'Request Free Inspection'}
                <i className="ri-arrow-right-line text-xl"></i>
              </button>

              {submitStatus === 'success' && (
                <div className="mt-4 p-4 bg-green-500/20 border border-green-500 rounded-lg text-white text-center">
                  Thank you! We'll contact you shortly.
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="mt-4 p-4 bg-red-500/20 border border-red-500 rounded-lg text-white text-center">
                  Something went wrong. Please try again or call us directly.
                </div>
              )}
            </form>

            {/* Trust Elements */}
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                <i className="ri-shield-check-line text-white text-lg"></i>
                <span className="text-white text-sm font-medium">Licensed #80007301</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                <i className="ri-checkbox-circle-line text-white text-lg"></i>
                <span className="text-white text-sm font-medium">Insured & Bonded</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                <i className="ri-award-line text-white text-lg"></i>
                <span className="text-white text-sm font-medium">A+ BBB Rating</span>
              </div>
            </div>
          </div>

          {/* Right Visual Zone */}
          <div className="lg:w-1/2 relative">
            <div className="relative h-full min-h-[500px] rounded-3xl overflow-hidden">
              <img 
                src="https://public.readdy.ai/ai/img_res/7847896e15c7bac3d832c893f1943770.jpg"
                alt="Quality Roofing"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#1B2838] to-transparent opacity-30"></div>

              {/* Floating Contact Card */}
              <div className="absolute bottom-8 right-8 bg-white rounded-2xl p-6 shadow-2xl w-80">
                <div className="text-[#1B2838] text-lg font-bold mb-3">Call Us Now</div>
                <a 
                  href="tel:405-397-2783"
                  className="text-[#FF6B35] text-4xl font-bold block mb-4 hover:text-[#ff5722] transition-colors cursor-pointer"
                >
                  405-397-2783
                </a>
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center gap-2 text-gray-600 text-sm">
                    <i className="ri-time-line text-base"></i>
                    <span>Available 24/7 for Emergencies</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}